import React, { useState, useEffect } from "react";
import { Card, Button, Modal, Form, Input, message, Alert, List, Avatar, Tag, Popconfirm, Empty } from "antd";
import { PlusOutlined, UploadOutlined, DeleteOutlined, FileTextOutlined, UserOutlined, HomeOutlined, PhoneOutlined } from "@ant-design/icons";
import { ProtectedFileLink, ProtectedImage, ProtectedImagePreviewGroup } from "../components/ProtectedFile";
import { PageLoadingOverlay } from "../components/RoomLoadingScene";

const residenceCache = new Map();

const MyResidenceScreen = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const cacheKey = user.id || "anonymous";
    const cachedResidence = residenceCache.get(cacheKey);
    const [rooms, setRooms] = useState(cachedResidence?.rooms || []);
    const [loading, setLoading] = useState(!cachedResidence);
    const [contractModal, setContractModal] = useState(false);
    const [residenceModal, setResidenceModal] = useState(false);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [contracts, setContracts] = useState([]);
    const [residences, setResidences] = useState([]);
    const [vacantRequests, setVacantRequests] = useState(cachedResidence?.vacantRequests || []);
    const [contractForm] = Form.useForm();
    const [residenceForm] = Form.useForm();
    const [residentCount, setResidentCount] = useState(1);

    useEffect(() => { loadMyRooms(Boolean(cachedResidence)); loadVacantRequests(); }, []);

    const loadMyRooms = async (quiet = false) => {
        try { if (!quiet) setLoading(true); const token = localStorage.getItem("token"); const res = await fetch("/api/my-rooms", { headers: { Authorization: "Bearer " + token } }); const data = await res.json(); setRooms(data); residenceCache.set(cacheKey, { ...(residenceCache.get(cacheKey) || {}), rooms: data }); } catch (e) {} finally { if (!quiet) setLoading(false); }
    };

    const loadVacantRequests = async () => {
        try { const token = localStorage.getItem("token"); const res = await fetch("/api/vacant-requests", { headers: { Authorization: "Bearer " + token } }); const data = await res.json(); setVacantRequests(data); residenceCache.set(cacheKey, { ...(residenceCache.get(cacheKey) || {}), vacantRequests: data }); } catch (e) {}
    };

    const loadDocuments = async (roomId) => {
        try { const token = localStorage.getItem("token"); const [cRes, rRes] = await Promise.all([fetch(`/api/rooms/${roomId}/contracts`, { headers: { Authorization: "Bearer " + token } }), fetch(`/api/rooms/${roomId}/residences`, { headers: { Authorization: "Bearer " + token } })]); setContracts(await cRes.json()); setResidences(await rRes.json()); } catch (e) {}
    };

    const handleConfirmVacant = async (requestId) => {
        try { const token = localStorage.getItem("token"); await fetch(`/api/vacant-requests/${requestId}/confirm`, { method: "PUT", headers: { Authorization: "Bearer " + token } }); message.success("已确认退租"); loadVacantRequests(); loadMyRooms(true); } catch (e) { message.error("操作失败"); }
    };

    const handleRequestVacant = (room) => {
        Modal.confirm({
            title: "申请退租",
            content: "确定要申请退租吗？提交后需要房东确认。",
            okText: "提交申请",
            cancelText: "取消",
            onOk: async () => {
                try {
                    const token = localStorage.getItem("token");
                    await fetch(`/api/rooms/${room.id}/tenant-request-vacant`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token }
                    });
                    message.success("退租申请已提交，等待房东确认");
                    loadMyRooms(true);
                } catch (e) { message.error("申请失败"); }
            }
        });
    };

    const handleUploadContract = async () => {
          try { 
              const values = await contractForm.validateFields(); 
              const token = localStorage.getItem("token"); 
              const res = await fetch("/api/contracts/upload", { 
                  method: "POST", 
                  headers: { "Content-Type": "application/json", Authorization: "Bearer " + token }, 
                  body: JSON.stringify({
                      room_id: selectedRoom.id,
                      title: values.title,
                      uploader_name: user.username,
                      uploader_role: user.role
                  })
              }); 
              const data = await res.json();
              if (res.ok) { 
                  message.success("合同上传成功"); 
                  setContractModal(false); 
                  contractForm.resetFields(); 
                  loadDocuments(selectedRoom.id); 
              } else { 
                  message.error(data.error || "上传失败"); 
              } 
          } catch (e) { 
              message.error("上传失败"); 
          }
      };

    const handleDeleteDocument = async (type, id) => {
        try { const token = localStorage.getItem("token"); const res = await fetch(`/api/documents/${type}/${id}`, { method: "DELETE", headers: { Authorization: "Bearer " + token } }); if (res.ok) { message.success("删除成功"); loadDocuments(selectedRoom.id); } else { message.error("删除失败"); } } catch (e) { message.error("删除失败"); }
    };

    const [residentFiles, setResidentFiles] = useState({});
    
    const handleResidentFileChange = (index, e) => {
        const file = e.target.files[0];
        if (file) {
            setResidentFiles(prev => ({ ...prev, [index]: file }));
        }
    };
    
    const handleUploadResidence = async () => {
          try {
              const values = await residenceForm.validateFields();
              const token = localStorage.getItem("token");
              
              for (let i = 0; i < residentCount; i++) {
                  const formData = new FormData();
                  formData.append("room_id", selectedRoom.id);
                  formData.append("name", values[`resident_name_${i}`]);
                  formData.append("phone", values[`resident_phone_${i}`] || "");
                  formData.append("id_card", values[`resident_id_card_${i}`]);
                  formData.append("uploader_name", user.username);
                  formData.append("uploader_role", user.role);
                  
                  if (residentFiles[i]) {
                      formData.append("file", residentFiles[i]);
                  }
                  
                  const res = await fetch("/api/residences/upload", {
                      method: "POST",
                      headers: { Authorization: "Bearer " + token },
                      body: formData
                  });
                  
                  if (!res.ok) {
                      message.error("上传失败");
                      return;
                  }
              }
              
              message.success("居住登记上传成功");
              setResidenceModal(false);
              residenceForm.resetFields();
              setResidentCount(1);
              setResidentFiles({});
              loadDocuments(selectedRoom.id);
          } catch (e) {
              message.error("上传失败");
          }
      };

    return (
        <div>
            {loading && <PageLoadingOverlay caption="正在加载住所信息，请稍候…" />}
            <Card title={<span><HomeOutlined /> 我的住所</span>}>
                {vacantRequests.length > 0 && (
                    <Alert message="退租通知" type="warning" showIcon style={{ marginBottom: 16 }}
                        description={
                            <div>
                                {vacantRequests.map(req => (
                                    <div key={req.id} style={{ marginTop: 8, padding: 8, background: "#fffbe6", borderRadius: 4 }}>
                                        <p><strong>{req.landlord_name}</strong> 发起了退租通知：{req.building_name} - {req.room_number}</p>
                                        <Popconfirm title="确定要确认退租吗？确认后房间将恢复为未租状态" onConfirm={() => handleConfirmVacant(req.id)} okText="确认退租" cancelText="取消">
                                            <Button type="primary" size="small" danger>确认退租</Button>
                                        </Popconfirm>
                                    </div>
                                ))}
                            </div>
                        }
                    />
                )}
                
                {rooms.length === 0 ? (
                    <Empty description="暂无租住的房间" />
                ) : (
                    <List itemLayout="vertical" dataSource={rooms} renderItem={room => (
                        <List.Item key={room.id} extra={
                            <div style={{ display: "flex", gap: 8 }}>
                                <Button type="primary" icon={<FileTextOutlined />} onClick={() => { setSelectedRoom(room); loadDocuments(room.id); setContractModal(true); }}>查看合同</Button>
                                <Button icon={<UserOutlined />} onClick={() => { setSelectedRoom(room); loadDocuments(room.id); setResidenceModal(true); }}>居住登记</Button>
                                <Popconfirm title="确定要申请退租吗？" onConfirm={() => handleRequestVacant(room)} okText="确定" cancelText="取消">
                                    <Button danger>申请退租</Button>
                                </Popconfirm>
                            </div>
                        }>
                            <List.Item.Meta 
                                avatar={<Avatar style={{ backgroundColor: "#1890ff" }} icon={<HomeOutlined />} />} 
                                title={<div><span>{room.building_name} - {room.room_number}</span><Tag color="red" style={{ marginLeft: 8 }}>已租</Tag></div>} 
                                description={
                                    <div>
                                        <p><HomeOutlined /> {room.region_name} {room.street_name} {room.building_name} {room.room_number}</p>
                                        <p><PhoneOutlined /> 房东：{room.landlord_name} | {room.landlord_phone}</p>
                                        <p>租金：¥{room.rent}/月 | 户型：{room.bedrooms}室{room.living_rooms}厅</p>
                                    </div>
                                } 
                            />
                        </List.Item>
                    )} />
                )}
            </Card>

            <Modal title="查看合同" open={contractModal} onCancel={() => setContractModal(false)} footer={[<Button key="close" onClick={() => setContractModal(false)}>关闭</Button>]} width={600}>
                  {contracts.length === 0 ? (
                      <Empty description="暂无合同" />
                  ) : (
                      <div>
                          <h4 style={{ marginBottom: 16 }}>已上传的合同</h4>
                          <ProtectedImagePreviewGroup preview={{ mask: "点击放大" }}>
                          <List size="small" dataSource={contracts} renderItem={item => (
                              <List.Item>
                                  <List.Item.Meta 
                                      avatar={<FileTextOutlined />} 
                                      title={item.title} 
                                      description={
                                          <div>
                                              {item.file_url && (
                                                  <div style={{ marginTop: 8 }}>
                                                      {item.file_type && item.file_type.startsWith("image/") ? (
                                                          <div>
                                                              <ProtectedImage document={item} alt={item.title} style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 4 }} preview />
                                                          </div>
                                                      ) : (
                                                          <div>
                                                              <FileTextOutlined /> 已上传文件
                                                          </div>
                                                      )}
                                                      <div style={{ marginTop: 4 }}>
                                                          <ProtectedFileLink document={item}>下载文件</ProtectedFileLink>
                                                      </div>
                                                  </div>
                                              )}
                                              <div style={{ color: "#8c8c8c", fontSize: 12, marginTop: 4 }}>
                                                  上传人：{item.uploader_name} ({item.uploader_role === "landlord" ? "房东" : "住户"})
                                                  <br />
                                                  上传时间：{new Date(item.created_at).toLocaleString()}
                                              </div>
                                          </div>
                                      } 
                                  />
                              </List.Item>
                          )} />
                          </ProtectedImagePreviewGroup>
                      </div>
                  )}
              </Modal>

            <Modal title="居住登记" open={residenceModal} onOk={handleUploadResidence} onCancel={() => setResidenceModal(false)} width={700}>
                <Form form={residenceForm} layout="vertical">
                    <div style={{ marginBottom: 16 }}>
                        <span>入住人数：</span>
                        <select value={residentCount} onChange={(e) => setResidentCount(parseInt(e.target.value))} style={{ marginLeft: 8, padding: "4px 8px" }}>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => <option key={n} value={n}>{n}人</option>)}
                        </select>
                    </div>
                    {Array.from({ length: residentCount }).map((_, index) => (
                        <div key={index} style={{ marginBottom: 16, padding: 12, background: "#f5f5f5", borderRadius: 4 }}>
                            <h4>入住人 {index + 1}</h4>
                            <Form.Item name={`resident_name_${index}`} label="姓名" rules={[{ required: true, message: "请输入姓名" }]}>
                                <Input placeholder="请输入姓名" />
                            </Form.Item>
                            <Form.Item name={`resident_phone_${index}`} label="电话">
                                <Input placeholder="请输入电话" />
                            </Form.Item>
                            <Form.Item name={`resident_id_card_${index}`} label="身份证号" rules={[{ required: true, message: "请输入身份证号" }]}>
                                <Input placeholder="请输入身份证号" />
                            </Form.Item>
                            <Form.Item label="身份证照片">
                                <input type="file" onChange={(e) => handleResidentFileChange(index, e)} accept=".jpg,.jpeg,.png" style={{ marginBottom: 8 }} />
                                {residentFiles[index] && <span style={{ color: "#1890ff" }}>{residentFiles[index].name}</span>}
                            </Form.Item>
                        </div>
                    ))}
                </Form>
                {residences.length > 0 && (
                    <div style={{ marginTop: 16, borderTop: "1px solid #f0f0f0", paddingTop: 16 }}>
                        <h4>已登记的居住人</h4>
                        <ProtectedImagePreviewGroup preview={{ mask: "点击放大" }}>
                        <List size="small" dataSource={residences} renderItem={item => (
                            <List.Item>
                                <List.Item.Meta
                                    avatar={<UserOutlined />}
                                    title={item.name}
                                    description={
                                        <div>
                                            {item.phone && <p>电话：{item.phone}</p>}
                                            {item.id_card && <p>身份证号：{item.id_card}</p>}
                                            {item.file_url && (
                                                <div style={{ marginTop: 8 }}>
                                                    <ProtectedImage document={item} alt="身份证照片" style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 4 }} preview />
                                                    <div style={{ marginTop: 4 }}>
                                                        <ProtectedFileLink document={item}>下载原图</ProtectedFileLink>
                                                    </div>
                                                </div>
                                            )}
                                            <p style={{ color: "#8c8c8c", fontSize: 12 }}>上传时间：{new Date(item.created_at).toLocaleString()}</p>
                                        </div>
                                    }
                                />
                            </List.Item>
                        )} />
                        </ProtectedImagePreviewGroup>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default MyResidenceScreen;
